import { ClickAwayListener, Popper, useTheme } from "@mui/material";
import AcxDatePicker from "components/UI/Calendar/AcxDatePicker";
import { Moment } from "moment";
import React, { useCallback, useState } from "react";
import AcxInputLabel from "../AcxInputLabel";
import Calendar from "./Calendar";

interface IAcxCalendarPopup {
    onSelect?: (selectedDate: moment.Moment) => void;
    currentDate?: Moment;
    inputLabel?: string;
    id: string;
    shrink?: boolean;
    disabled?: boolean;
    required?: boolean;
    popperPlacement?: "left";
    containerStyle?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
}

export default function AcxCalendarPopup(props: IAcxCalendarPopup) {
    const theme = useTheme();
    const [openPopover, setOpenPopover] = React.useState(false);
    const popRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [selectedDate, setSelectedDate] = React.useState<
        Moment | undefined
    >();

    const [labelFocus, setLabelFocus] = useState<boolean | undefined>();

    const dateSelect = useCallback(
        (dt: moment.Moment) => {
            setSelectedDate(dt);
            if (props.onSelect) {
                props.onSelect(dt);
            }
            setOpenPopover(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.onSelect],
    );

    function handleClickAway(event) {
        setOpenPopover(false);
    }

    React.useEffect(() => {
        setSelectedDate(props.currentDate);
    }, [props.currentDate]);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div style={props.containerStyle}>
                {props.inputLabel && (
                    <AcxInputLabel
                        shrink={props.shrink}
                        focused={labelFocus}
                        sx={{
                            fontFamily: theme.typography.fontFamily,
                            color: theme.palette.text.primary,
                            fontSize: "12px",
                            lineHeight: "16px",
                        }}
                        id={props.id + "date-picker-label"}
                    >
                        {props.inputLabel}{" "}
                        {props.required && !props.currentDate && (
                            <span style={{ color: theme.palette.error.main }}>
                                *
                            </span>
                        )}
                    </AcxInputLabel>
                )}
                <div ref={containerRef}>
                    <AcxDatePicker
                        id={props.id}
                        onFocus={() => setLabelFocus(true)}
                        onBlur={() => setLabelFocus(false)}
                        disabled={props.disabled}
                        selectedDate={selectedDate}
                        onDateChange={dateSelect}
                        onCalendarClick={() => {
                            setOpenPopover(!openPopover);
                        }}
                        inputStyle={props.inputStyle}
                    />
                </div>
                <div style={{ position: "relative" }} ref={popRef}></div>

                <Popper
                    sx={{ zIndex: 1 }}
                    style={{ zIndex: 10000 }}
                    anchorEl={containerRef.current}
                    placement={props.popperPlacement ?? "bottom-start"}
                    open={openPopover}
                    disablePortal={false}
                    modifiers={[
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            enabled: true,
                            options: {
                                boundariesElement: "window", // Note: 'boundariesElement' has been replaced with 'boundary' in Popper.js v2
                            },
                        },
                    ]}
                >
                    <div
                        style={{
                            boxShadow: theme.shadows[1],
                            backgroundColor: theme.palette.white.main,
                            padding: theme.spacing(1),
                        }}
                    >
                        <Calendar onSelection={dateSelect} />
                    </div>
                </Popper>
            </div>
        </ClickAwayListener>
    );
}

AcxCalendarPopup.defaultProps = {
    shrink: false,
};
